import { vclog } from '../hooks/vc-log'

/**
 * What happens when a denoiser changes its mind after `process()` returned.
 *
 * `NoiseCancellation` can come good late and it can die late. Late: a context
 * that needed a user gesture is retried on the next click, so the denoised
 * stream turns up long after the raw microphone was published. Dead: the
 * browser suspends a context that was running — headphones plugged in, the
 * machine sleeping, an iOS call — and the published track carries silence.
 * Only the hook that owns the producer can move it, so both cases call back
 * to it through these two functions.
 *
 * Every call site used to pass neither. The retry is only armed when
 * `onLateReady` is given, so it never was: a meeting joined without a click
 * ran the whole call undenoised while Settings said otherwise. And the silence
 * watcher had nobody to tell, so a suspended context left the room seeing
 * video and hearing no voice — the bug the watcher was written for.
 *
 * Bound to ONE denoiser. A callback from one that has since been replaced, by
 * a mic switch or by turning noise cancellation off, must not move the
 * producer off its successor.
 */

/** The part of `NoiseCancellation` this needs. */
export type Denoiser = {
  stop(): Promise<void>
}

/** The part of a mediasoup producer this needs. */
export type AudioSender = {
  readonly closed: boolean
  replaceTrack(options: { track: MediaStreamTrack }): Promise<void>
}

/** The hook's side: its refs, read at the moment a callback fires. */
export type DenoiserHost = {
  /** The denoiser the hook holds now, if any. */
  current(): Denoiser | null
  /** Forget the current denoiser. */
  clear(): void
  producer(): AudioSender | null
  /** The raw microphone — what goes back on air when denoising dies. */
  raw(): MediaStreamTrack | null
  micOn(): boolean
  /** The published track changed; mirror it into the local stream. */
  published(track: MediaStreamTrack): void
  /** Queue behind every other change to the published microphone. */
  run(op: () => Promise<void>): Promise<void>
}

export type DenoiserCallbacks = {
  onLateReady: (stream: MediaStream) => void
  onSilenced: () => void
}

export function denoiserRecovery(denoiser: Denoiser, host: DenoiserHost): DenoiserCallbacks {
  return {
    onLateReady(stream) {
      void host.run(async () => {
        const track = stream.getAudioTracks()[0]
        if (host.current() !== denoiser || !track) {
          // Replaced while it waited. The graph it just built has no owner.
          await denoiser.stop()
          return
        }
        const ap = host.producer()
        if (!ap || ap.closed) {
          // Nothing to move yet. Let it go rather than hold a graph nobody
          // publishes: once the producer exists, the toggle effect finds no
          // denoiser and builds one — and there has been a gesture by then.
          host.clear()
          await denoiser.stop()
          return
        }
        // Muted stays muted, same as every other swap.
        track.enabled = host.micOn()
        await ap.replaceTrack({ track })
        host.published(track)
        vclog('producer', 'noise cancellation arrived late — producer moved onto it', {})
      })
    },

    onSilenced() {
      void host.run(async () => {
        if (host.current() !== denoiser) return
        host.clear()
        const ap = host.producer()
        const raw = host.raw()
        if (ap && !ap.closed && raw && raw.readyState === 'live') {
          raw.enabled = host.micOn()
          await ap.replaceTrack({ track: raw })
          host.published(raw)
          vclog('producer', 'raw microphone back on air after the denoiser went silent', {})
        }
        await denoiser.stop()
      })
    },
  }
}
